import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { 
  Trophy, 
  Crown, 
  Sword, 
  Shield, 
  Star,
  Target,
  Zap,
  Award,
  Medal,
  Sparkles
} from "lucide-react";

interface UserBadge {
  id: string;
  badge_type: string;
  earned_at: string;
  tutorial_id?: string;
}

interface BadgeDisplayProps {
  userId?: string; // Optional: view other players' badges, defaults to current user
  layout?: 'grid' | 'horizontal';
  maxDisplay?: number;
  showDetails?: boolean;
}

// Feature flags - hide broken features
const TUTORIALS_ENABLED = false;
const PUZZLES_ENABLED = false;

// Tutorial and puzzle related badges to hide when features are disabled
const TUTORIAL_BADGES = ['castling_pro', 'knight_ninja', 'piece_master', 'opening_expert', 'first_steps', 'tutorial_champion'];
const PUZZLE_BADGES = ['mate_finder', 'tactics_guru', 'endgame_wizard'];

const BADGE_CONFIG = {
  'hatchling': {
    name: 'Hatchling',
    description: 'Completed your first chess game',
    icon: Sparkles,
    color: 'bg-gradient-to-br from-amber-400 to-orange-500'
  },
  'castling_pro': {
    name: 'Castling Pro',
    description: 'Mastered the art of castling',
    icon: Shield,
    color: 'bg-blue-500'
  },
  'knight_ninja': {
    name: 'Knight Ninja',
    description: 'Learned the L-shaped jump',
    icon: Sword,
    color: 'bg-purple-500'
  },
  'mate_finder': {
    name: 'Mate Finder',
    description: 'Discovered checkmate patterns',
    icon: Target,
    color: 'bg-red-500'
  },
  'pawn_promoter': {
    name: 'Pawn Promoter',
    description: 'Promoted a pawn to queen',
    icon: Crown,
    color: 'bg-yellow-500'
  },
  'piece_master': {
    name: 'Piece Master',
    description: 'Learned all piece movements',
    icon: Star,
    color: 'bg-green-500'
  },
  'opening_expert': {
    name: 'Opening Expert',
    description: 'Mastered opening principles',
    icon: Zap,
    color: 'bg-orange-500'
  },
  'tactics_guru': {
    name: 'Tactics Guru',
    description: 'Solved tactical puzzles',
    icon: Award,
    color: 'bg-indigo-500'
  },
  'endgame_wizard': {
    name: 'Endgame Wizard',
    description: 'Conquered endgame challenges',
    icon: Medal,
    color: 'bg-pink-500'
  },
  'first_steps': {
    name: 'First Steps',
    description: 'Started the chess journey',
    icon: Sparkles,
    color: 'bg-cyan-500'
  },
  'tutorial_champion': {
    name: 'Tutorial Champion',
    description: 'Completed all tutorials',
    icon: Trophy,
    color: 'bg-amber-500'
  }
};

export function BadgeDisplay({ 
  userId,
  layout = 'grid', 
  maxDisplay = 10, 
  showDetails = true 
}: BadgeDisplayProps) {
  const { user } = useAuth();
  const targetUserId = userId || user?.id;
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (targetUserId) {
      fetchBadges();
    } else {
      setLoading(false);
    }
  }, [targetUserId]);

  const fetchBadges = async () => {
    if (!targetUserId) return;

    try {
      const { data, error } = await supabase
        .from('user_badges')
        .select('*')
        .eq('user_id', targetUserId)
        .order('earned_at', { ascending: false });

      if (error) throw error;
      setBadges(data || []);
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const awardBadge = async (badgeType: string, tutorialId?: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_badges')
        .insert({
          user_id: user.id,
          badge_type: badgeType,
          tutorial_id: tutorialId
        });

      if (error && !error.message.includes('duplicate key')) {
        throw error;
      }

      // Refresh badges
      fetchBadges();
      
      const badgeConfig = BADGE_CONFIG[badgeType as keyof typeof BADGE_CONFIG];
      if (badgeConfig) {
        toast({
          title: "🏆 Badge Earned!",
          description: `You've earned the "${badgeConfig.name}" badge!`,
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Card className="p-4 text-center">
        <p className="text-muted-foreground">Sign in to earn and display badges!</p>
      </Card>
    );
  }

  // Filter out tutorial/puzzle badges when features are disabled
  const filteredBadges = badges.filter(badge => {
    if (!TUTORIALS_ENABLED && TUTORIAL_BADGES.includes(badge.badge_type)) return false;
    if (!PUZZLES_ENABLED && PUZZLE_BADGES.includes(badge.badge_type)) return false;
    return true;
  });

  if (filteredBadges.length === 0) {
    return (
      <Card className="p-4 text-center">
        <div className="space-y-2">
          <Trophy className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">No badges yet!</p>
          <p className="text-sm text-muted-foreground">
            Play games and improve your chess skills to earn badges!
          </p>
        </div>
      </Card>
    );
  }

  const displayBadges = filteredBadges.slice(0, maxDisplay);

  if (layout === 'horizontal') {
    return (
      <div className="flex flex-wrap gap-2">
        {displayBadges.map((badge) => {
          const config = BADGE_CONFIG[badge.badge_type as keyof typeof BADGE_CONFIG];
          if (!config) return null;
          
          const IconComponent = config.icon;
          
          return (
            <Badge
              key={badge.id}
              variant="secondary"
              className={`${config.color} text-white border-none flex items-center gap-1`}
              title={showDetails ? config.description : undefined}
            >
              <IconComponent className="w-3 h-3" />
              {config.name}
            </Badge>
          );
        })}
        {filteredBadges.length > maxDisplay && (
          <Badge variant="outline">
            +{filteredBadges.length - maxDisplay} more
          </Badge>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {displayBadges.map((badge) => {
        const config = BADGE_CONFIG[badge.badge_type as keyof typeof BADGE_CONFIG];
        if (!config) return null;
        
        const IconComponent = config.icon;
        
        return (
          <Card key={badge.id} className="p-3 text-center hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className={`w-10 h-10 ${config.color} rounded-full flex items-center justify-center mx-auto`}>
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-medium text-sm">{config.name}</h4>
                {showDetails && (
                  <p className="text-xs text-muted-foreground">{config.description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(badge.earned_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// Export the award badge function for use in tutorials
export const useBadgeAward = () => {
  const { user } = useAuth();
  
  const awardBadge = async (badgeType: string, tutorialId?: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_badges')
        .insert({
          user_id: user.id,
          badge_type: badgeType,
          tutorial_id: tutorialId
        });

      if (error && !error.message.includes('duplicate key')) {
        throw error;
      }

      const badgeConfig = BADGE_CONFIG[badgeType as keyof typeof BADGE_CONFIG];
      if (badgeConfig && !error) {
        toast({
          title: "🏆 Badge Earned!",
          description: `You've earned the "${badgeConfig.name}" badge!`,
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Error awarding badge:', error);
    }
  };

  return { awardBadge };
};